import intersection from 'lodash/intersection';

import { organizationTypeId as OrganizationTypeId } from '@shared/constants';

//TODO: Might be good to check if this file is still needed now that we have the new permissions
import {
  adminAkelaRoles,
  canApproveSBRoles,
  canEditParticipationStatusRoles,
  canEditPaymentLogsRoles,
  canEditPermissionsManagerRoles,
  canEditUnitPaymentLogsRoles,
  canEditYouthProfileRoles,
  mBCounselorRoles,
  organizationRoles,
  scoutbookRoles,
  specialLogInRoles,
} from './constants';
import type { DedupedRoleType } from './types';

/**
 * Checks whether a role can access a unit context (e.g. Unit Dropdown)
 * This doesn't necessarily grant admin privileges because `organizationRoles.ADMIN` includes all `scoutbookRoles`.
 */
export const hasAdminRights = ({ role, roleTypes }: DedupedRoleType) =>
  organizationRoles.ADMIN.includes(role) ||
  roleTypes.some(({ roleType }) => organizationRoles.ADMIN.includes(roleType));

export const hasSpecialLoginRole = ({ role }: DedupedRoleType) =>
  (specialLogInRoles as string[]).includes(role);

export const hasAdvancementsCoordinatorRights = (org: { role: string }) =>
  org.role === organizationRoles.ADVANCEMENT_COORDINATOR;

export const getCouncilRights = ({
  role,
  roleTypes,
  organizationTypeId,
}: DedupedRoleType) => {
  const hasCouncilEditRights = organizationRoles.COUNCIL_ADMINS.includes(role);
  const hasCouncilAdvancement =
    organizationRoles.COUNCIL_ADVANCEMENT_COORDINATOR.includes(role);
  const hasExtraCouncilRole =
    Number(organizationTypeId) === OrganizationTypeId.COUNCIL &&
    (organizationRoles.EXTRA_COUNCIL_ROLES.includes(role) ||
      roleTypes.some(({ roleType }) =>
        organizationRoles.EXTRA_COUNCIL_ROLES.includes(roleType),
      ));

  const isCouncil =
    hasCouncilEditRights ||
    hasCouncilAdvancement ||
    hasExtraCouncilRole ||
    organizationRoles.DISTRICT_EXECUTIVES.includes(role);

  return {
    isCouncil,
    hasCouncilEditRights,
    hasExtraCouncilRole,
  };
};

export const hasCouncilRights = (org: DedupedRoleType) =>
  getCouncilRights(org).isCouncil;

export const hasMeritBadgeCounselRights = (dedupedRole: DedupedRoleType) =>
  organizationRoles.MERIT_BADGE_COUNSELOR === dedupedRole.role ||
  dedupedRole.allRoles?.some(
    roleName => roleName === organizationRoles.MERIT_BADGE_COUNSELOR,
  );

/**
 * Whether the user counsels merit badges and also holds a leader role in the
 * same organization, which is common for adults who help their unit beyond
 * counseling. Parent and youth roles do not count as leader roles, so a
 * counselor who is also a parent is still only a counselor here.
 *
 * The advancements a counselor may work with are restricted to merit badges,
 * while a leader gets the ones of the unit program. This tells both apart, as
 * hasMeritBadgeCounselRights is true for either of them.
 */
export const hasMBCAndLeaderRights = (dedupedRole: DedupedRoleType) =>
  hasMeritBadgeCounselRights(dedupedRole) &&
  (dedupedRole.allRoles ?? []).some(
    roleName =>
      roleName !== organizationRoles.MERIT_BADGE_COUNSELOR &&
      !organizationRoles.PARENT_GUARDIAN.includes(roleName) &&
      !organizationRoles.YOUTH_MEMBER.includes(roleName),
  );

export const hasYouthMemberRights = ({ role, roleTypes }: DedupedRoleType) =>
  organizationRoles.YOUTH_MEMBER.includes(role) ||
  roleTypes.some(({ roleType }) =>
    organizationRoles.YOUTH_MEMBER.includes(roleType),
  );

export const hasParentGuardianRights = ({ role, roleTypes }: DedupedRoleType) =>
  organizationRoles.PARENT_GUARDIAN.includes(role) ||
  roleTypes.some(({ roleType }) =>
    organizationRoles.PARENT_GUARDIAN.includes(roleType),
  );

export const hasAdminAkelaRole = ({ role, roleTypes }: DedupedRoleType) =>
  adminAkelaRoles.includes(role) ||
  roleTypes.some(({ roleType }) => adminAkelaRoles.includes(roleType));

export const hasApproverSBRole = ({ role, roleTypes }: DedupedRoleType) =>
  canApproveSBRoles.includes(role) ||
  roleTypes.some(({ roleType }) => canApproveSBRoles.includes(roleType));

export const hasScoutbookRole = ({ role, roleTypes }: DedupedRoleType) =>
  scoutbookRoles.includes(role) ||
  roleTypes.some(({ roleType }) => scoutbookRoles.includes(roleType));

export const hasMBCRights = ({
  role,
  roleTypes,
  allRoles = [],
}: DedupedRoleType) =>
  mBCounselorRoles.includes(role) ||
  roleTypes.some(({ roleType }) => mBCounselorRoles.includes(roleType)) ||
  allRoles.some(rolName => mBCounselorRoles.includes(rolName));

export const hasEditYouthProfileRolesPerms = ({
  role,
  roleTypes,
  allRoles,
}: DedupedRoleType) =>
  canEditYouthProfileRoles.includes(role) ||
  roleTypes.some(({ roleType }) =>
    canEditYouthProfileRoles.includes(roleType),
  ) ||
  allRoles.some(rolName => canEditYouthProfileRoles.includes(rolName));

export const hasEditPaymentLogsPerms = ({ allRoles }: DedupedRoleType) =>
  allRoles.some(name => canEditPaymentLogsRoles.includes(name));

export const hasEditUnitPaymentLogsPerms = ({ allRoles }: DedupedRoleType) =>
  allRoles.some(name => canEditUnitPaymentLogsRoles.includes(name));

export const hasPermissionsManagerRights = ({
  role,
  roleTypes,
  allRoles = [],
}: DedupedRoleType) =>
  canEditPermissionsManagerRoles.includes(role) ||
  organizationRoles.PERMISSIONS_MANAGER.includes(role) ||
  roleTypes.some(({ roleType }) =>
    canEditPermissionsManagerRoles.includes(roleType),
  ) ||
  allRoles.some(rolName => canEditPermissionsManagerRoles.includes(rolName));

export function hasEditOptOutPerms(
  userRole: Pick<DedupedRoleType, 'role' | 'roleTypes' | 'allRoles'>,
) {
  if (canEditParticipationStatusRoles.includes(userRole.role)) return true;

  const acceptedRoles = [
    ...(userRole.roleTypes?.map(item => item.roleType) || []),
    ...(userRole.allRoles || []),
  ].filter(Boolean);
  const validFound = intersection(
    acceptedRoles,
    canEditParticipationStatusRoles,
  );
  return validFound.length > 0;
}
